/**
 * Service pour la gestion des produits dans le restaurant.
 *
 * Fournit des méthodes pour :
 * - createProduct : créer un produit.
 * - getProduct : récupérer la liste des produits avec pagination et filtres.
 * - getProductById : récupérer un produit par son ID.
 * - UpdateProduct : mettre à jour un produit existant.
 * - DeleteProduct : supprimer un produit.
 * - uploadImage : uploader une image de produit.
 * - createRecommendation : ajouter une recommandation pour un produit.
 * - deleteRecommendation : supprimer une recommandation pour un produit.
 *
 * Utilise ax
 * - IPoductBody : structure des données d’un produit.iosInstance pour les requêtes HTTP avec interceptors configurés.
 * Types utilisés :
 * - IApiResponseProduct : structure de la réponse API pour les produits.
 * - IProductImage : structure pour l’upload d’une image de produit.
 * - IProductRelationBody : structure pour créer une recommandation.
 * - IProductRecommendationRemoveBody : structure pour supprimer une recommandation.
 * - IUpdateProductBody : structure pour la mise à jour d’un produit.
 * - AxiosError : type pour gérer les erreurs des requêtes HTTP.
 *
 * Références externes :
 * - token : contient les tokens d’authentification.
 * - pageProduct : numéro de page pour la pagination.
 * - designation_product_filter : filtres par désignation.
 * - category_product_filter : filtres par catégorie.
 * - restaurantId : identifiant du restaurant courant.
 */
import { toRaw } from 'vue';
import type {
  IApiResponseProduct,
  IPoductBody,
  IProductImage,
  IProductRecommendationRemoveBody,
  IProductRelationBody,
  IUpdateProductBody,
  IBodySetVisibility,
  IBodyAddAddonGroup,
  IBodyUpdateAddonGroup,
  IBodyAddAddonOption,
  IAddonGroupsResponse,
} from '@/types/product.types';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import { recommendationId, restaurantId, token } from '@/refs/common.refs';
import axios from 'axios';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import {
  pageProduct,
  sortProduct,
  designation_product_filter,
  category_product_filter,
  addonGroupId,
  addonId,
  searchQueryProduct,
  currentLocale,
} from '@/refs/product.refs';
import { commerceId } from '@/refs/commerce.refs';

export const ProductService = {
  /**
   * Crée un nouveau produit.
   * @param data - Données du produit (IPoductBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createProduct: async (data: IPoductBody) => {
    try {
      const response = axiosInstance.post(`products/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des produits avec pagination et filtres par désignation et catégorie.
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getProduct: async (organizationId?: string) => {
    try {
      const page = Number(toRaw(pageProduct.value));
      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());
      if (organizationId) params.append('organizationId', organizationId);
      else params.append('restau', commerceId.value);

      const search = toRaw(searchQueryProduct.value);
      if (search) params.append('designation', search);

      params.append('sort', toRaw(sortProduct.value));

      designation_product_filter.value.forEach((designation) => {
        params.append('designation', designation);
      });
      category_product_filter.value.forEach((categoryId) => {
        params.append('categoryIds', categoryId);
      });

      const response = await axiosInstance.get<IApiResponseProduct>(
        `products/search?${params.toString()}`,
        {
          headers: {
            'accept-language': currentLocale.value,
          },
        },
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  getProductForCollections: async (organizationId?: string) => {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      else params.append('restau', commerceId.value);
      params.append('pageSize', '500');
      const response = await axiosInstance.get<IApiResponseProduct>(
        `products/search?${params.toString()}`,
        { headers: { 'accept-language': currentLocale.value } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Récupère un produit par son ID.
   * @param productId - ID du produit
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getProductById: async (productId: string) => {
    try {
      const response = await axiosInstance.get<IApiResponseProduct>(
        `products/get/${productId}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour un produit existant.
   * @param data - Données à mettre à jour (IUpdateProductBody)
   * @param productId - ID du produit
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  UpdateProduct: async (data: IUpdateProductBody, productId: string) => {
    try {
      const response = axiosInstance.put(`products/update/${productId}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un produit par son ID.
   * @param productId - ID du produit
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  DeleteProduct: async (productId: string) => {
    try {
      const response = axiosInstance.delete(`products/delete/${productId}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Upload une image pour un produit.
   * @param data - Fichier et métadonnées pour l'image (IProductImage)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  uploadImage: async (data: IProductImage) => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('designation', data.designation);
      formData.append('restaurantId', data.restaurantId);

      const accessToken = token.value?.accessToken;
      const response = await axios.post(
        `${VITE_API_URL}files/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Crée une recommandation pour un produit.
   * @param data - Données de la relation de recommandation (IProductRelationBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createRecommendation: async (data: IProductRelationBody) => {
    try {
      const response = axiosInstance.put(`products/recommend`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une recommandation pour un produit.
   * @param data - Données pour supprimer la recommandation (IProductRecommendationRemoveBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  deleteRecommendation: async (data: IProductRecommendationRemoveBody) => {
    try {
      const response = await axiosInstance.delete(`products/unrecommend`, {
        headers: { 'Content-Type': 'application/json' },
        data: data,
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Change la visibilité d'un produit.
   * PUT /api/products/update/{productId}/visibility
   */
  setVisibility: async (productId: string, data: IBodySetVisibility) => {
    try {
      const response = await axiosInstance.put(
        `products/update/${productId}/visibility`,
        data,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  // ─────────────────────────────────────────────────────────────
  // ADDON GROUPS (extras)
  // ─────────────────────────────────────────────────────────────

  /**
   * Crée un addonGroup pour un produit.
   * POST /api/product/{productId}/extras
   */
  addAddonGroup: async (productId: string, data: IBodyAddAddonGroup) => {
    try {
      const response = await axiosInstance.post(
        `product/${productId}/extras`,
        data,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Récupère les addonGroups d'un produit.
   * GET /api/product/{productId}/extras
   */
  getAddonGroups: async (productId: string) => {
    try {
      const response = await axiosInstance.get<IAddonGroupsResponse>(
        `product/${productId}/extras`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Met à jour un addonGroup.
   * PUT /api/extras/{addonGroupId}
   */
  updateAddonGroup: async (data: IBodyUpdateAddonGroup) => {
    try {
      const id = toRaw(addonGroupId.value);
      if (!id) return;
      const response = await axiosInstance.put(`extras/${id}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Supprime un addonGroup.
   * DELETE /api/extras/{addonGroupId}
   */
  deleteAddonGroup: async () => {
    try {
      const id = toRaw(addonGroupId.value);
      if (!id) return;
      const response = await axiosInstance.delete(`extras/${id}`);
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Ajoute une option dans un extra.
   * POST /api/extra/{addonGroupId}/options
   */
  addAddonOption: async (data: IBodyAddAddonOption) => {
    try {
      const id = toRaw(addonGroupId.value);
      if (!id) return;
      const response = await axiosInstance.post(`extra/${id}/options`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Met à jour un addon (option).
   * PUT /api/addon/{addonId}
   */
  updateAddon: async (data: Record<string, any>) => {
    try {
      const id = toRaw(addonId.value);
      if (!id) return;
      const response = await axiosInstance.put(`addon/${id}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Supprime un addon (option).
   * DELETE /api/addon/{addonId}
   */
  deleteAddon: async () => {
    try {
      const id = toRaw(addonId.value);
      if (!id) return;
      const response = await axiosInstance.delete(`addon/${id}`);
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Récupère la liste des produits avec pagination et filtres par désignation et catégorie.
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getRecomandation: async () => {
    try {
      const page = Number(toRaw(pageProduct.value));
      const params = new URLSearchParams();
      // params.append("pagination", "true");
      // params.append("page", page.toString());
      // params.append("restau", restaurantId.value);
      if (restaurantId.value) params.append('restaurantId', restaurantId.value);
      if (recommendationId.value)
        params.append('recommendationId', recommendationId.value);

      const response = await axiosInstance.get<any>(
        `products/search-recommendation?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
